import { Component, OnDestroy, OnInit } from '@angular/core';

import { from, of, Subject, Subscription } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';

import { userPermissionsEnum } from '../../../../../Core/Users/enums/user-permissions.enum';
import { CurrentUserStateService } from '../../../../../Core/Users/services/current-user-state.service';
import { ConfigService } from '../../../../../Core/Config/services/config.service';

import { MediaServiceApi } from '../../../../../api/mediaService/media-service.api';
import { ScheduleType } from '../../../../../Common/Forms/components/DeliveryScheduleInput/models/delivery-schedule';
import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';
import { DistributionService } from '../../../../../Common/TargetingShared/services/distribution.service';
import { HeaderEditorService } from '../../../../../Common/TargetingShared/services/header-editor.service';
import { TargetingPreferencesService } from '../../../../../Common/TargetingShared/services/targeting-preferences.service';
import { AbstractDialogComponent } from '../../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { DiscoverRoutingService } from '../../../../../Discover/DiscoverRouter/services/discover-routing.service';
import { DistributionModel } from '../../../models/distribution.model';
import { PostDistributionAmplifyDialogComponent } from '../PostDistributionAmplifyDialog/post-distribution-amplify-dialog.component';
import { DistributionStatus } from '@Common/TargetingShared/enum/distribution-status.enum';
import { AmplificationStatusAPIEnum } from '../../../../../api/distributions/enum/amplification-status-api.enum';


@Component({
    selector: 'agpr-distribution-confirm-dialog',
    templateUrl: './distribution-confirmation.component.html',
    styleUrls: ['./distribution-confirmation.component.scss'],
    providers: [
        HeaderEditorService,
        {
            provide: MediaServiceApi,
            useClass: MediaServiceApi
        }
    ],
    standalone: false
})
export class DistributionConfirmationComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    public readonly buttonTypes = ButtonTypesEnum;
    public readonly messageSeverityEnum = messageSeverityEnum;

    distribution: DistributionModel;
    scheduledTime: string = '';
    deliveryTime: string;
    loading = false;
    hide = false;
    disclaimerApproved: boolean = false;

    private subscriptions: Subscription[] = [];
    private sendDistributionSubject$ = new Subject<void>();

    constructor(
        public coreDialogService: CoreDialogService,
        public discoverRoutingService: DiscoverRoutingService,
        public distributionService: DistributionService,
        public headerEditorService: HeaderEditorService,
        public mediaServiceApi: MediaServiceApi,
        public targetingPreferencesService: TargetingPreferencesService,
        private currentUserService: CurrentUserStateService,
        private configService: ConfigService,
    ) {
        super(coreDialogService, 'DistributionConfirmation');

    }

    ngOnInit() {
        const sendDistribution$ = this.sendDistributionSubject$.pipe(
            tap(() => {
                this.loading = true;
            }),
            switchMap(() => this.distributionService.getFocusedResource().pipe(filter(x=> !!x), take(1))),
            switchMap((focusedDistribution) => {
                if (focusedDistribution?.id) {
                    return of(null);
                }
                return this.distributionService.saveDistribution(true);
            }),
            switchMap(() => {
                return this.distributionService.getFocusedResource().pipe(
                    filter(x => !!x),
                    take(1),
                    switchMap((distribution) => {
                        if (distribution.onlineBoostApproved) {
                            return this.distributionService.setDistributionPostOptions(distribution.id, [AmplificationStatusAPIEnum.ONLINE_BOOST]);
                        }
                        return of(null);
                    })
                );
            }),
            switchMap(() => from(this.distributionService.scheduleDistribution(this.scheduledTime))),
            // TODO: Uncomment Newsroom when NEP-13769 lands
            switchMap(() => this.currentUserService.hasAnyPermissions([ /* userPermissionsEnum.newsroom, */ userPermissionsEnum.newswireplus])),
            switchMap((hasPermissions) => {
                const isWhiteLabeled = !!this.configService.hasBrandGUID();

                if (hasPermissions || !isWhiteLabeled) {
                    return this.distributionService.getFocusedResource().pipe(filter(x=> !!x));
                }
                return of(null);
            }),
            switchMap((focusedDistribution) => {
                if (focusedDistribution && focusedDistribution.status !== DistributionStatus.draft) {
                    this.loading = false;
                    this.hide = true;
                    this.closeDialog(true);
                    return this.coreDialogService.openWithObservable(PostDistributionAmplifyDialogComponent);
                }
                this.loading = false;
                this.hide = true;
                this.closeDialog(true);
                return of(null);
            }),
            catchError(err => {
                console.log(err);
                this.hide = false;
                this.loading = false;
                return of(null);
            })
        );

        this.subscriptions.push(
            this.distributionService.getFocusedResource().pipe(
                filter(x => !!x)
            ).subscribe(distribution => {
                this.distribution = distribution;
                const scheduleTime = this.distribution.deliverySchedule;
                if (scheduleTime.scheduleType === ScheduleType.immediate) {
                    this.scheduledTime = 'Immediately';
                    this.deliveryTime = 'sent immediately';
                } else {
                    this.scheduledTime = scheduleTime.getDisplayString();
                    this.deliveryTime = 'sent on ' + this.scheduledTime;
                }
            }),
            sendDistribution$.subscribe()
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    sendDistribution() {
        this.sendDistributionSubject$.next();
    }

}
